import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import FullScreenLoader from "../components/FullScreenLoader";
import WelcomeHeader from "components/Home/WelcomeHeader";
import { PageLayout } from "components/Layout/PageLayout";
import InfiniteScroll from "../components/InfiniteScroll";
import { PendantOperation } from "../components/PendantOperation";
import { useGetOperationQuery } from 'redux/api/operationApi';
import { useDispatch } from "react-redux";
import { useAppSelector } from "redux/store";
import { IOperationState } from "redux/features/operationSlice";
import { OperationStatus } from "Enums/OperationStatus";
import { useIntl } from "react-intl";

const PendantPage = () => {
  const { formatMessage } = useIntl();
  const [minDate,setMinDate] = useState<Date>();
  const [maxDate,setMaxDate] = useState<Date>();
  const [operationId,setOperationId] = useState<string>("");
  const [clientName,setClientName] = useState<string>("");

  const [debouncedFilters, setDebouncedFilters] = useState<{
    status: OperationStatus;
    operationDateMin: Date | undefined;
    operationDateMax: Date | undefined;
    id: string;
    clientName: string;
  }>({
    status: OperationStatus.PENDING,
    operationDateMin: undefined,
    operationDateMax: undefined,
    id: "",
    clientName: "",
  });

  const dispatch = useDispatch();
  const { pageIndex, pageSize, sort, filters } = useAppSelector(
    (state: { operationState: IOperationState }) => state.operationState
  );

  const { data: fetchedOperations, isLoading, isFetching } = useGetOperationQuery({
    filters: {...filters, ...debouncedFilters},
    pageIndex: pageIndex,
    pageSize: pageSize,
    sort: sort,
  });

  const operations = fetchedOperations?.data || [];
  const total = fetchedOperations?.total ?? 0;
  const hasMore = total > operations.length;

  const loadMore = useCallback(async () => {
    if (!isFetching && hasMore) {
      // Actualizar el pageIndex de forma asincrónica
      dispatch({ type: "operation/setPageIndex", payload: pageIndex + 1 });
    }
  }, [pageIndex, isFetching, hasMore, dispatch]);

  // Lógica de debounce con `useEffect`
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilters({
        status: OperationStatus.PENDING,
        operationDateMin: minDate,
        operationDateMax: maxDate,
        id: operationId,
        clientName: clientName,
      });
    }, 500); // 500ms de debounce

    return () => {
      clearTimeout(handler); // Limpia el timeout si el usuario sigue escribiendo
    };
  }, [minDate, maxDate, operationId, clientName]);

  if (isLoading && pageIndex === 1) {
    return <FullScreenLoader />;
  }

  return (
    <PageLayout>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="flex-start"
        alignItems="center"
        sx={{
          flex: 1,
          width: "100%",
          backgroundColor: "#d9d9d9",
          padding: "20px",
          height: "100%",
        }}
      >
        <Typography
            variant="h1"
            sx={{
              textAlign: "center",
              fontWeight: "bold",
            }}
          >{formatMessage({ id: "pendant.title" })}</Typography>
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '20px', // Espacio entre los elementos
              marginBottom: '10px',
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography>{formatMessage({ id: "history.from" })}</Typography>
              <input
                type="date"
                onChange={(e) => setMinDate(new Date(e.target.value))}
                style={{
                  width: '100%',
                  padding: '8px',
                  fontSize: '16px',
                  backgroundColor: '#f1f1f1',
                  borderColor: '#000000',
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography>{formatMessage({ id: "history.until" })}</Typography>
              <input
                type="date"
                onChange={(e) => setMaxDate(new Date(e.target.value))}
                style={{
                  width: '100%',
                  padding: '8px',
                  fontSize: '16px',
                  backgroundColor: '#f1f1f1',
                  borderColor: '#000000',
                }}
              />
            </Box>
          </Box>
          <Box>
            <Box>
              <Typography>{formatMessage({ id: "history.operation.id" })}</Typography>
              <input
                type="text"
                onChange={(e) => setOperationId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  marginBottom: '10px',
                  fontSize: '16px',
                  backgroundColor: '#f1f1f1',
                  borderColor: '#000000',
                }}
              />
            </Box>
            <Box>
              <Typography>{formatMessage({ id: "history.client.name" })}</Typography>
              <input
                type="text"
                onChange={(e) => setClientName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  marginBottom: '20px',
                  fontSize: '16px',
                  backgroundColor: '#f1f1f1',
                  borderColor: '#000000',
                }}
              />
            </Box>
          </Box>
        </Box>

        <InfiniteScroll
          items={operations}
          loadMore={loadMore}
          hasMore={hasMore}
          isLoading={isFetching}
          renderItem={(operation) => (
            <PendantOperation operation={operation} />
          )}
        />
      </Box>
    </PageLayout>
  );
};

export default PendantPage;
